<template>
  <Layout>
    <div class="source-analytics">
      <!-- UTM Tutorial Banner (padrão ai-banner) -->
      <div v-if="!hideUtmBanner" class="ai-banner ai-banner--info ai-banner--clickable" @click="showUtmModal = true">
        <div class="ai-banner-icon">
          <i class="bx bx-link" style="font-size: 18px; color: #fff;"></i>
        </div>
        <div class="ai-banner-content">
          <span class="ai-banner-title">Maximize seu rastreamento com UTM Parameters</span>
          <span class="ai-banner-subtitle">Rastreie campanhas do Instagram, Facebook, Google Ads e mais. Clique para ver o tutorial.</span>
        </div>
        <div class="ai-banner-badge">UTM</div>
        <button class="ai-banner-close" @click.stop="dismissBanner">
          <i class="bx bx-x"></i>
        </button>
      </div>

      <!-- Header -->
      <div class="sa-header">
        <div>
          <h4 class="sa-title">Analytics de Origem</h4>
          <p class="sa-subtitle" v-if="lastUpdate">Atualizado há {{ timeAgo }}</p>
        </div>
        <div class="sa-header-right">
          <!-- Period Tabs -->
          <div class="sa-tabs">
            <button
              v-for="range in rangeOptions"
              :key="range.value"
              type="button"
              class="sa-tab"
              :class="{ active: selectedRange === range.value }"
              @click="selectRange(range.value)"
            >{{ range.label }}</button>
            <span class="sa-tab-sep"></span>
            <div class="sa-date-picker-wrap">
              <button type="button" class="sa-tab" :class="{ active: selectedRange === 'custom' }" @click="showDatePicker = !showDatePicker" title="Período personalizado">
                <i class="mdi mdi-calendar-range"></i>
              </button>
              <div v-if="showDatePicker" class="sa-date-dropdown">
                <label class="sa-date-dropdown-label">De</label>
                <input type="date" v-model="customDates.start" class="sa-date-dropdown-input" :max="customDates.end" />
                <label class="sa-date-dropdown-label">Até</label>
                <input type="date" v-model="customDates.end" class="sa-date-dropdown-input" :min="customDates.start" />
                <div class="sa-date-dropdown-actions">
                  <button type="button" class="sa-date-dropdown-btn cancel" @click="showDatePicker = false; clearFilters()">Limpar</button>
                  <button type="button" class="sa-date-dropdown-btn apply" @click="applyCustomDates">Aplicar</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Metric Toggle -->
          <div class="sa-tabs">
            <button
              type="button"
              class="sa-tab"
              :class="{ active: selectedMetric === 'sessions' }"
              @click="selectMetric('sessions')"
            ><i class="mdi mdi-eye-outline mr-1"></i> Visitantes</button>
            <button
              type="button"
              class="sa-tab"
              :class="{ active: selectedMetric === 'conversions' }"
              @click="selectMetric('conversions')"
            ><i class="mdi mdi-check-circle-outline mr-1"></i> Conversões</button>
          </div>

          <!-- Export -->
          <button type="button" class="sa-btn-export" @click="exportData" :disabled="loading">
            <i class="mdi mdi-download mr-1"></i> Exportar
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading && !analytics" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted small">Carregando dados...</p>
      </div>

      <!-- Dashboard -->
      <template v-else-if="analytics">
        <!-- KPI Row -->
        <div class="kpi-row">
          <div class="kpi-card" style="animation-delay: 0.05s">
            <div class="kpi-label">Visitantes</div>
            <div class="kpi-value primary">{{ formatNumber(analytics.total_sessions || 0) }}</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.08s">
            <div class="kpi-label">Conversões</div>
            <div class="kpi-value success">{{ formatNumber(analytics.total_converted || 0) }}</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.11s">
            <div class="kpi-label">Taxa Conversão</div>
            <div class="kpi-value success">{{ analytics.conversion_rate || 0 }}%</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.14s">
            <div class="kpi-label">Taxa Rejeição</div>
            <div class="kpi-value danger">{{ analytics.bounce_rate || 0 }}%</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.17s">
            <div class="kpi-label">Tempo Médio</div>
            <div class="kpi-value">{{ formatTime(analytics.avg_time_on_page) }}</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.20s">
            <div class="kpi-label">Pág./Sessão</div>
            <div class="kpi-value">{{ analytics.avg_pages_per_session || 0 }}</div>
          </div>
        </div>

        <!-- Hero Chart: Timeline -->
        <div class="sa-card hero-chart">
          <div class="hero-chart-header">
            <div>
              <div class="hero-chart-title">Visitantes ao Longo do Tempo</div>
              <div class="hero-chart-subtitle">{{ selectedMetric === 'conversions' ? 'Conversões' : 'Visitantes' }} no período selecionado</div>
            </div>
          </div>
          <apexchart v-if="analytics.timeline && analytics.timeline.length > 0" type="area" height="260" :options="timelineChartOptions" :series="timelineChartSeries" />
          <div v-else class="empty-state-card">
            <div class="empty-icon"><i class="mdi mdi-chart-line-variant"></i></div>
            <div class="empty-title">Nenhum dado de timeline</div>
            <div class="empty-desc">Os dados aparecerão quando houver visitantes no período</div>
          </div>
        </div>

        <!-- Mid Cards: Sources, Devices, Mediums -->
        <div class="cards-grid">
          <!-- Sources -->
          <div class="sa-card">
            <div class="sa-card-header">
              <div class="sa-card-title">
                <i class="mdi mdi-source-branch mr-2"></i>
                Fontes de Tráfego
              </div>
            </div>
            <div v-if="analytics.top_sources && analytics.top_sources.length > 0" class="source-list">
              <div v-for="(item, idx) in analytics.top_sources.slice(0, 6)" :key="idx" class="source-item">
                <div class="source-icon-box" :style="{ background: getSourceColor(item.source).bg, color: getSourceColor(item.source).text }">
                  <i :class="getSourceIcon(item.source)"></i>
                </div>
                <div class="source-content">
                  <div class="source-row">
                    <span class="source-name">{{ item.label }}</span>
                    <div class="source-meta">
                      <span class="source-count">{{ item.total }}</span>
                      <span class="source-pct">{{ getPercentage(item.total) }}%</span>
                    </div>
                  </div>
                  <div class="source-bar">
                    <div class="source-bar-fill" :style="{ width: getBarWidth(item.total, analytics.top_sources) + '%', background: getSourceColor(item.source).bar }"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="mdi mdi-source-branch"></i></div>
              <div class="empty-title">Nenhuma fonte registrada</div>
              <div class="empty-desc">As fontes aparecerão com os primeiros visitantes</div>
            </div>
          </div>

          <!-- Devices -->
          <div class="sa-card">
            <div class="sa-card-header">
              <div class="sa-card-title">
                <i class="mdi mdi-devices mr-2"></i>
                Dispositivos
              </div>
            </div>
            <div v-if="analytics.by_device && analytics.by_device.length > 0" class="donut-layout">
              <div class="donut-chart-wrap">
                <apexchart type="donut" height="160" width="160" :options="deviceChartOptions" :series="deviceChartSeries" />
              </div>
              <div class="donut-legend">
                <div v-for="(device, idx) in analytics.by_device" :key="idx" class="donut-legend-item">
                  <div class="donut-legend-dot" :style="{ background: chartColors[idx % chartColors.length] }"></div>
                  <div class="donut-legend-info">
                    <span class="donut-legend-name">{{ capitalize(device.device_type || 'Desconhecido') }}</span>
                    <span class="donut-legend-val">{{ device.total }} — {{ getPercentage(device.total) }}%</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="mdi mdi-cellphone-off"></i></div>
              <div class="empty-title">Nenhum dado de dispositivo</div>
              <div class="empty-desc">Os dados aparecerão com os primeiros visitantes</div>
            </div>
          </div>

          <!-- Mediums -->
          <div class="sa-card">
            <div class="sa-card-header">
              <div class="sa-card-title">
                <i class="mdi mdi-swap-horizontal mr-2"></i>
                Meios de Tráfego
              </div>
              <span class="sa-card-subtitle">Canais de aquisição</span>
            </div>
            <div v-if="analytics.top_mediums && analytics.top_mediums.length > 0">
              <div v-for="(item, idx) in analytics.top_mediums.slice(0, 6)" :key="idx" class="medium-item">
                <div class="medium-icon-box" :style="{ background: getMediumColor(item.medium).bg, color: getMediumColor(item.medium).text }">
                  <i :class="getMediumIcon(item.medium)"></i>
                </div>
                <div class="medium-info">
                  <div class="medium-name">{{ item.label }}</div>
                  <div class="medium-sub">{{ getMediumDescription(item.medium) }}</div>
                </div>
                <div class="medium-stats">
                  <div class="medium-count">{{ item.total }}</div>
                  <div class="medium-pct">{{ getPercentage(item.total) }}%</div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="mdi mdi-swap-horizontal-variant"></i></div>
              <div class="empty-title">Nenhum meio registrado</div>
              <div class="empty-desc">Os meios aparecerão com os primeiros visitantes</div>
            </div>
          </div>
        </div>

        <!-- Bottom Cards: Location, Campaigns, Languages -->
        <div class="cards-grid">
          <!-- Location (Tabbed) -->
          <div class="sa-card">
            <div class="sa-card-header">
              <div class="sa-card-title">
                <i class="mdi mdi-earth mr-2"></i>
                Localização
              </div>
              <div class="card-tabs">
                <button class="card-tab" :class="{ active: locationTab === 'countries' }" @click="locationTab = 'countries'">Países</button>
                <button class="card-tab" :class="{ active: locationTab === 'regions' }" @click="locationTab = 'regions'">Regiões</button>
              </div>
            </div>
            <!-- Countries -->
            <div v-show="locationTab === 'countries'">
              <template v-if="analytics.by_country && analytics.by_country.length > 0">
                <div v-for="(item, idx) in analytics.by_country.slice(0, 8)" :key="'c'+idx" class="geo-item">
                  <span class="geo-flag">{{ getCountryFlag(item.country) }}</span>
                  <div class="geo-info">
                    <div class="geo-name">{{ item.country || 'Desconhecido' }}</div>
                    <div class="geo-bar"><div class="geo-bar-fill" :style="{ width: getBarWidth(item.total, analytics.by_country) + '%' }"></div></div>
                  </div>
                  <div class="geo-stats">
                    <div class="geo-count">{{ item.total }}</div>
                    <div class="geo-pct">{{ getPercentage(item.total) }}%</div>
                  </div>
                </div>
              </template>
              <div v-else class="empty-state-card">
                <div class="empty-icon"><i class="mdi mdi-earth-off"></i></div>
                <div class="empty-title">Nenhum dado de país</div>
                <div class="empty-desc">Dados geográficos requerem IP público</div>
              </div>
            </div>
            <!-- Regions -->
            <div v-show="locationTab === 'regions'">
              <template v-if="hasRegionData">
                <div v-for="(item, idx) in locationRegions.slice(0, 8)" :key="'r'+idx" class="geo-item">
                  <span class="geo-flag"><i class="mdi mdi-map-marker" style="font-size: 20px; color: #8b90a5;"></i></span>
                  <div class="geo-info">
                    <div class="geo-name">{{ item.region || item.state || 'Desconhecido' }}</div>
                    <div class="geo-bar"><div class="geo-bar-fill" :style="{ width: getBarWidth(item.total, locationRegions) + '%' }"></div></div>
                  </div>
                  <div class="geo-stats">
                    <div class="geo-count">{{ item.total }}</div>
                    <div class="geo-pct">{{ getPercentage(item.total) }}%</div>
                  </div>
                </div>
              </template>
              <div v-else class="empty-state-card">
                <div class="empty-icon"><i class="mdi mdi-map-marker-off"></i></div>
                <div class="empty-title">Nenhum dado de região</div>
                <div class="empty-desc">Dados geográficos requerem IP público</div>
              </div>
            </div>
          </div>

          <!-- Campaigns -->
          <div class="sa-card">
            <div class="sa-card-header">
              <div class="sa-card-title">
                <i class="mdi mdi-bullhorn mr-2"></i>
                Top Campanhas
              </div>
            </div>
            <div v-if="analytics.top_campaigns && analytics.top_campaigns.length > 0" class="table-wrapper">
              <table class="modern-table">
                <thead>
                  <tr>
                    <th>Campanha</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in analytics.top_campaigns.slice(0, 8)" :key="idx">
                    <td><div class="campaign-name">{{ item.campaign }}</div></td>
                    <td class="text-right"><span class="value-badge">{{ item.total }}</span></td>
                    <td class="text-right"><span class="text-muted small">{{ getPercentage(item.total) }}%</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="mdi mdi-bullhorn-outline"></i></div>
              <div class="empty-title">Nenhuma campanha registrada</div>
              <div class="empty-desc">Configure UTM parameters para rastrear suas campanhas de marketing</div>
            </div>
          </div>

          <!-- Languages -->
          <div class="sa-card">
            <div class="sa-card-header">
              <div class="sa-card-title">
                <i class="mdi mdi-translate mr-2"></i>
                Idiomas
              </div>
            </div>
            <div v-if="analytics.by_language && analytics.by_language.length > 0">
              <div v-for="(item, idx) in analytics.by_language.slice(0, 6)" :key="idx" class="lang-item">
                <span class="lang-code">{{ item.language || '??' }}</span>
                <div class="lang-bar-wrap">
                  <div class="lang-bar">
                    <div class="lang-bar-fill" :style="{ width: getBarWidth(item.total, analytics.by_language) + '%', background: getLanguageBarColor(idx) }">
                      {{ getPercentage(item.total) }}%
                    </div>
                  </div>
                  <span class="lang-count">{{ item.total }}</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="mdi mdi-translate-off"></i></div>
              <div class="empty-title">Nenhum dado de idioma</div>
              <div class="empty-desc">Os idiomas são detectados automaticamente do navegador</div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div v-else class="empty-state-page">
        <i class="mdi mdi-chart-line-variant"></i>
        <p>Nenhum dado de analytics disponível</p>
      </div>
    </div>

    <!-- Modal Tutorial UTM -->
    <b-modal v-model="showUtmModal" title="Como Criar Links com UTM" size="lg" hide-footer scrollable>
      <div class="utm-tutorial">
        <div class="utm-section">
          <h6 class="text-primary mb-3">O que são UTM Parameters?</h6>
          <p>
            UTM Parameters são tags adicionadas no final das URLs para rastrear a origem do tráfego.
            Com eles, você sabe exatamente de onde vêm seus visitantes: Instagram, Facebook, Google Ads, etc.
          </p>
        </div>
        <div class="utm-section">
          <h6 class="text-primary mb-3">Estrutura básica</h6>
          <div class="utm-example">
            <code>https://seuevento.com.br?utm_source=instagram&amp;utm_medium=social&amp;utm_campaign=lancamento</code>
          </div>
        </div>
        <div class="utm-section">
          <h6 class="text-primary mb-3">Parâmetros principais</h6>
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Parâmetro</th>
                <th>Descrição</th>
                <th>Exemplo</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>utm_source</code></td>
                <td>Origem do tráfego</td>
                <td>instagram, facebook, google, linkedin</td>
              </tr>
              <tr>
                <td><code>utm_medium</code></td>
                <td>Tipo de canal</td>
                <td>social, cpc, email, referral</td>
              </tr>
              <tr>
                <td><code>utm_campaign</code></td>
                <td>Nome da campanha</td>
                <td>blackfriday2024, lancamento</td>
              </tr>
              <tr>
                <td><code>utm_content</code></td>
                <td>Variação do anúncio</td>
                <td>banner-azul, video-testimonial</td>
              </tr>
              <tr>
                <td><code>utm_term</code></td>
                <td>Palavra-chave (ads)</td>
                <td>evento-online, workshop</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="utm-section">
          <h6 class="text-primary mb-3">Exemplos práticos</h6>
          <div class="utm-use-case mb-3">
            <div class="use-case-title">
              <i class="mdi mdi-instagram text-danger mr-2"></i>
              Story do Instagram
            </div>
            <div class="use-case-url">
              <code>https://seuevento.com.br?utm_source=instagram&amp;utm_medium=social&amp;utm_campaign=story_lancamento</code>
            </div>
          </div>
          <div class="utm-use-case mb-3">
            <div class="use-case-title">
              <i class="mdi mdi-facebook text-primary mr-2"></i>
              Anúncio Facebook Ads
            </div>
            <div class="use-case-url">
              <code>https://seuevento.com.br?utm_source=facebook&amp;utm_medium=cpc&amp;utm_campaign=ads_publico_quente</code>
            </div>
          </div>
          <div class="utm-use-case mb-3">
            <div class="use-case-title">
              <i class="mdi mdi-email text-success mr-2"></i>
              Email Marketing
            </div>
            <div class="use-case-url">
              <code>https://seuevento.com.br?utm_source=email&amp;utm_medium=newsletter&amp;utm_campaign=black_friday</code>
            </div>
          </div>
          <div class="utm-use-case mb-3">
            <div class="use-case-title">
              <i class="mdi mdi-whatsapp text-success mr-2"></i>
              Link do WhatsApp
            </div>
            <div class="use-case-url">
              <code>https://seuevento.com.br?utm_source=whatsapp&amp;utm_medium=social&amp;utm_campaign=divulgacao_grupos</code>
            </div>
          </div>
        </div>
        <div class="utm-section">
          <h6 class="text-primary mb-3">Ferramentas para criar UTMs</h6>
          <ul>
            <li>
              <strong>Google Campaign URL Builder:</strong>
              <a href="https://ga-dev-tools.google/campaign-url-builder/" target="_blank">
                ga-dev-tools.google/campaign-url-builder/
              </a>
            </li>
            <li>
              <strong>UTM.io:</strong>
              <a href="https://utm.io" target="_blank">utm.io</a>
            </li>
          </ul>
        </div>
        <div class="utm-section">
          <div class="alert alert-success">
            <i class="mdi mdi-lightbulb-on mr-2"></i>
            <strong>Dica:</strong> Use nomes consistentes e descritivos. Evite espaços e caracteres especiais.
            Use hífen (-) ou underline (_) para separar palavras.
          </div>
        </div>
        <div class="text-right mt-4">
          <b-button variant="primary" @click="showUtmModal = false">
            Entendi!
          </b-button>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import appConfig from '@/app.config'
import http from '@/http'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'SourceAnalytics',
  components: {
    Layout,
    apexchart: VueApexCharts
  },
  page: {
    title: 'Analytics de Origem',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    return {
      loading: false,
      analytics: null,

      // UTM Banner/Modal
      showUtmModal: false,
      hideUtmBanner: localStorage.getItem('hideUtmBanner') === 'true',

      // Range selection
      selectedRange: '7d',
      rangeOptions: [
        { label: '24h', value: '24h' },
        { label: '7d', value: '7d' },
        { label: '30d', value: '30d' },
        { label: '90d', value: '90d' }
      ],
      customDates: {
        start: null,
        end: null
      },

      // Metric
      selectedMetric: 'sessions',

      // Location tab
      locationTab: 'countries',

      // Date picker dropdown
      showDatePicker: false,

      // Chart colors
      chartColors: ['#556ee6', '#34c38f', '#f1b44c', '#50a5f1', '#f46a6a'],

      // Auto-refresh & elapsed time
      refreshInterval: null,
      lastUpdate: null,
      elapsedTime: 0,
      elapsedInterval: null
    }
  },
  computed: {
    hasDemographicData () {
      return (this.analytics?.by_country?.length > 0) ||
             (this.analytics?.by_city?.length > 0) ||
             (this.analytics?.by_language?.length > 0)
    },

    hasRegionData () {
      return (this.analytics?.by_region?.length > 0) || (this.analytics?.by_state?.length > 0)
    },

    locationRegions () {
      return this.analytics?.by_region || this.analytics?.by_state || []
    },

    timeAgo () {
      if (!this.lastUpdate || this.elapsedTime === 0) return '0s'
      const seconds = this.elapsedTime
      if (seconds < 60) return `${seconds}s`
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes} min`
      const hours = Math.floor(minutes / 60)
      return `${hours}h`
    },

    // Timeline Chart (Area)
    timelineChartSeries () {
      return [{
        name: this.selectedMetric === 'conversions' ? 'Conversões' : 'Visitantes',
        data: this.analytics?.timeline?.map(t => t.total) || []
      }]
    },
    timelineChartOptions () {
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          sparkline: { enabled: false }
        },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
          type: 'gradient',
          gradient: {
            opacityFrom: 0.5,
            opacityTo: 0.05
          }
        },
        colors: ['#556ee6'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.analytics?.timeline?.map(t => this.formatDate(t.date)) || [],
          labels: { style: { fontSize: '11px', colors: '#8b90a5' } }
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px', colors: ['#8b90a5'] },
            formatter: (val) => Number.isInteger(val) ? val : ''
          }
        },
        grid: { borderColor: '#eaecf2', strokeDashArray: 4 },
        tooltip: { theme: 'light' }
      }
    },

    // Device Chart (Donut with custom legend)
    deviceChartSeries () {
      if (!this.analytics?.by_device) return []
      return this.analytics.by_device.map(d => d.total)
    },
    deviceChartOptions () {
      return {
        labels: this.analytics?.by_device?.map(d => this.capitalize(d.device_type || 'Desconhecido')) || [],
        colors: this.chartColors,
        legend: { show: false },
        chart: { type: 'donut' },
        dataLabels: { enabled: false },
        stroke: { show: false },
        plotOptions: { pie: { donut: { size: '70%' } } },
        tooltip: { enabled: true }
      }
    }
  },
  mounted () {
    this.loadData()
    this.startAutoRefresh()
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        const params = this.getRangeParams()
        const response = await http.get('/admin/analytics/sources', { params })
        this.analytics = response.data
        this.lastUpdate = Date.now()
        this.elapsedTime = 0
      } catch (error) {
        console.error('Erro ao carregar analytics:', error)
      } finally {
        this.loading = false
      }
    },

    getRangeParams () {
      const params = {}

      if (this.selectedMetric === 'conversions') {
        params.metric = 'conversions'
      }

      if (this.selectedRange === 'custom') {
        params.start_date = this.customDates.start
        params.end_date = this.customDates.end
        return params
      }

      const now = new Date()
      let startDate

      switch (this.selectedRange) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
          break
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
          break
        default:
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      }

      params.start_date = startDate.toISOString().split('T')[0]
      params.end_date = now.toISOString().split('T')[0]
      return params
    },

    selectRange (range) {
      this.selectedRange = range
      this.customDates = { start: null, end: null }
      this.loadData()
    },

    selectMetric (metric) {
      this.selectedMetric = metric
      this.loadData()
    },

    onCustomDateChange () {
      if (this.customDates.start && this.customDates.end) {
        this.selectedRange = 'custom'
        this.loadData()
      }
    },

    applyCustomDates () {
      if (this.customDates.start && this.customDates.end) {
        this.selectedRange = 'custom'
        this.showDatePicker = false
        this.loadData()
      }
    },

    clearFilters () {
      this.customDates = { start: null, end: null }
      this.selectedRange = '7d'
      this.loadData()
    },

    async exportData () {
      try {
        const params = this.getRangeParams()
        const response = await http.get('/admin/analytics/sources/export', { params, responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `analytics-origem-${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$bvToast.toast('Dados exportados!', { title: 'Sucesso', variant: 'success', solid: true })
      } catch (error) {
        this.$bvToast.toast('Erro ao exportar.', { title: 'Erro', variant: 'danger', solid: true })
      }
    },

    dismissBanner () {
      this.hideUtmBanner = true
      localStorage.setItem('hideUtmBanner', 'true')
    },

    startAutoRefresh () {
      this.refreshInterval = setInterval(() => {
        this.loadData()
      }, 120000)

      this.elapsedInterval = setInterval(() => {
        this.elapsedTime++
      }, 1000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) clearInterval(this.refreshInterval)
      if (this.elapsedInterval) clearInterval(this.elapsedInterval)
    },

    getSourceIcon (source) {
      const icons = {
        instagram: 'bx bxl-instagram',
        facebook: 'bx bxl-facebook',
        linkedin: 'bx bxl-linkedin',
        twitter: 'bx bxl-twitter',
        google: 'bx bxl-google',
        whatsapp: 'bx bxl-whatsapp',
        youtube: 'bx bxl-youtube',
        tiktok: 'bx bxl-tiktok',
        email: 'mdi mdi-email-outline',
        direct: 'mdi mdi-arrow-right-bold'
      }
      return icons[source] || 'bx bx-link'
    },

    getSourceColor (source) {
      const s = (source || '').toLowerCase()
      const colors = {
        direct: { bg: 'rgba(85, 110, 230, 0.08)', text: '#556ee6', bar: '#556ee6' },
        instagram: { bg: 'rgba(225, 48, 108, 0.08)', text: '#e1306c', bar: '#e1306c' },
        facebook: { bg: 'rgba(24, 119, 242, 0.08)', text: '#1877f2', bar: '#1877f2' },
        google: { bg: 'rgba(234, 67, 53, 0.08)', text: '#ea4335', bar: '#ea4335' },
        whatsapp: { bg: 'rgba(37, 211, 102, 0.08)', text: '#25d366', bar: '#25d366' },
        linkedin: { bg: 'rgba(0, 119, 181, 0.08)', text: '#0077b5', bar: '#0077b5' },
        twitter: { bg: 'rgba(29, 161, 242, 0.08)', text: '#1da1f2', bar: '#1da1f2' },
        youtube: { bg: 'rgba(255, 0, 0, 0.08)', text: '#ff0000', bar: '#ff0000' },
        email: { bg: 'rgba(255, 152, 0, 0.08)', text: '#ff9800', bar: '#ff9800' },
        tiktok: { bg: 'rgba(0, 0, 0, 0.06)', text: '#333', bar: '#333' }
      }
      return colors[s] || { bg: 'rgba(139, 144, 165, 0.08)', text: '#8b90a5', bar: '#8b90a5' }
    },

    getCountryFlag (country) {
      const c = (country || '').toLowerCase().trim()
      const flags = {
        'brasil': '\u{1F1E7}\u{1F1F7}',
        'brazil': '\u{1F1E7}\u{1F1F7}',
        'estados unidos': '\u{1F1FA}\u{1F1F8}',
        'united states': '\u{1F1FA}\u{1F1F8}',
        'portugal': '\u{1F1F5}\u{1F1F9}',
        'argentina': '\u{1F1E6}\u{1F1F7}',
        'colombia': '\u{1F1E8}\u{1F1F4}',
        'col\u00f4mbia': '\u{1F1E8}\u{1F1F4}',
        'mexico': '\u{1F1F2}\u{1F1FD}',
        'm\u00e9xico': '\u{1F1F2}\u{1F1FD}',
        'chile': '\u{1F1E8}\u{1F1F1}',
        'spain': '\u{1F1EA}\u{1F1F8}',
        'espanha': '\u{1F1EA}\u{1F1F8}',
        'united kingdom': '\u{1F1EC}\u{1F1E7}',
        'reino unido': '\u{1F1EC}\u{1F1E7}',
        'germany': '\u{1F1E9}\u{1F1EA}',
        'alemanha': '\u{1F1E9}\u{1F1EA}',
        'france': '\u{1F1EB}\u{1F1F7}',
        'fran\u00e7a': '\u{1F1EB}\u{1F1F7}',
        'italy': '\u{1F1EE}\u{1F1F9}',
        'it\u00e1lia': '\u{1F1EE}\u{1F1F9}',
        'japan': '\u{1F1EF}\u{1F1F5}',
        'jap\u00e3o': '\u{1F1EF}\u{1F1F5}',
        'canada': '\u{1F1E8}\u{1F1E6}',
        'canad\u00e1': '\u{1F1E8}\u{1F1E6}',
        'peru': '\u{1F1F5}\u{1F1EA}',
        'uruguay': '\u{1F1FA}\u{1F1FE}',
        'uruguai': '\u{1F1FA}\u{1F1FE}',
        'paraguay': '\u{1F1F5}\u{1F1FE}',
        'paraguai': '\u{1F1F5}\u{1F1FE}',
        'india': '\u{1F1EE}\u{1F1F3}',
        '\u00edndia': '\u{1F1EE}\u{1F1F3}',
        'china': '\u{1F1E8}\u{1F1F3}',
        'australia': '\u{1F1E6}\u{1F1FA}',
        'austr\u00e1lia': '\u{1F1E6}\u{1F1FA}'
      }
      return flags[c] || '\u{1F30D}'
    },

    getMediumDescription (medium) {
      const descs = {
        organic: 'Busca org\u00e2nica',
        cpc: 'Tr\u00e1fego pago (CPC)',
        social: 'Redes sociais',
        email: 'Campanhas de e-mail',
        referral: 'Links de refer\u00eancia',
        direct: 'Acesso direto',
        video: 'Conte\u00fado em v\u00eddeo',
        none: 'Tr\u00e1fego sem atribui\u00e7\u00e3o'
      }
      return descs[(medium || '').toLowerCase()] || 'Outro canal'
    },

    getLanguageBarColor (idx) {
      const palette = ['#556ee6', '#34c38f', '#f1b44c', '#8b5cf6', '#50a5f1', '#f46a6a']
      return palette[idx % palette.length]
    },

    getBarWidth (value, items) {
      if (!items || !items.length || !value) return 0
      const max = Math.max(...items.map(i => i.total || 0))
      if (max === 0) return 0
      return Math.round((value / max) * 100)
    },

    getPercentage (value) {
      if (!this.analytics?.total_sessions) return 0
      return ((value / this.analytics.total_sessions) * 100).toFixed(1)
    },

    getDevicePercentage (type) {
      if (!this.analytics?.by_device || !this.analytics.total) return 0
      const device = this.analytics.by_device.find(d => d.device_type === type)
      if (!device) return 0
      return ((device.total / this.analytics.total) * 100).toFixed(0)
    },

    formatDate (dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    },

    capitalize (str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    },

    formatTime (seconds) {
      if (!seconds || seconds === 0) return '0s'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      if (mins > 0) {
        return `${mins}m ${secs}s`
      }
      return `${secs}s`
    },

    formatNumber (num) {
      if (!num) return '0'
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    },

    getMediumIcon (medium) {
      const icons = {
        organic: 'mdi mdi-magnify',
        cpc: 'mdi mdi-currency-usd',
        social: 'mdi mdi-share-variant',
        email: 'mdi mdi-email-outline',
        referral: 'mdi mdi-link-variant',
        direct: 'mdi mdi-arrow-right-bold',
        video: 'mdi mdi-play-circle-outline',
        none: 'mdi mdi-help-circle-outline'
      }
      return icons[medium] || 'mdi mdi-web'
    },

    getMediumColor (medium) {
      const colors = {
        organic: { bg: '#e3f2fd', text: '#1976d2', bar: '#2196f3' },
        cpc: { bg: '#f3e5f5', text: '#7b1fa2', bar: '#9c27b0' },
        social: { bg: '#e8f5e9', text: '#388e3c', bar: '#4caf50' },
        email: { bg: '#fff3e0', text: '#e65100', bar: '#ff9800' },
        referral: { bg: '#fce4ec', text: '#c2185b', bar: '#e91e63' },
        direct: { bg: '#e0f2f1', text: '#00695c', bar: '#009688' },
        video: { bg: '#ffebee', text: '#c62828', bar: '#f44336' },
        none: { bg: '#f5f5f5', text: '#616161', bar: '#9e9e9e' }
      }
      return colors[medium] || { bg: '#f5f5f5', text: '#616161', bar: '#9e9e9e' }
    }
  }
}
</script>

<style scoped>
/* === ANIMATIONS === */
@keyframes slideUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* === HEADER === */
.sa-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
}

.sa-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1d26;
  margin: 0 0 1px 0;
}

.sa-subtitle {
  color: #adb5bd;
  font-weight: 400;
  font-size: 14px;
  margin: 0;
}

.sa-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* === TABS / FILTER BAR === */
.sa-tabs {
  display: flex;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  height: 38px;
  align-items: center;
}

.sa-tab {
  background: none;
  border: none;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #8b90a5;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.sa-tab:hover {
  color: #1a1d26;
  background: #f5f6fa;
}

.sa-tab.active {
  background: #556ee6;
  color: #fff;
  box-shadow: 0 2px 8px rgba(85, 110, 230, 0.3);
}

.sa-tab-sep {
  width: 1px;
  height: 20px;
  background: #dee2e6;
  margin: 0 4px;
}

.sa-date-input {
  border: none;
  background: transparent;
  padding: 4px 8px;
  font-size: 12px;
  color: #495057;
  outline: none;
  width: 130px;
}

.sa-tab-label {
  font-size: 11px;
  color: #adb5bd;
  padding: 0 2px;
}

/* === EXPORT BUTTON === */
.sa-btn-export {
  padding: 8px 18px;
  background: #556ee6;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.sa-btn-export:hover {
  background: #4560d4;
  box-shadow: 0 4px 12px rgba(85, 110, 230, 0.3);
}

.sa-btn-export:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* === KPI ROW === */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.kpi-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.kpi-card:hover {
  border-color: #d4d8e3;
  box-shadow: 0 4px 16px rgba(26, 29, 38, 0.06);
}

.kpi-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8b90a5;
  font-weight: 600;
  margin-bottom: 10px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1;
}

.kpi-value.primary { color: #556ee6; }
.kpi-value.success { color: #34c38f; }
.kpi-value.danger { color: #f46a6a; }

/* === CARDS GRID === */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

/* === CARD === */
.sa-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 280px;
  max-height: 480px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.sa-card::-webkit-scrollbar {
  width: 6px;
}

.sa-card::-webkit-scrollbar-track {
  background: transparent;
}

.sa-card::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.sa-card::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

.sa-card:hover {
  border-color: #d4d8e3;
  box-shadow: 0 4px 16px rgba(26, 29, 38, 0.06);
}

.sa-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.sa-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  display: flex;
  align-items: center;
}

.sa-card-subtitle {
  font-size: 11px;
  color: #8b90a5;
}

/* === HERO CHART === */
.hero-chart {
  margin-bottom: 24px;
  min-height: auto;
  max-height: none;
  overflow: visible;
}

.hero-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.hero-chart-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1d26;
}

.hero-chart-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin-top: 2px;
}

/* === SOURCE LIST === */
.source-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.source-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.source-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.source-content {
  flex: 1;
  min-width: 0;
}

.source-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.source-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
}

.source-meta {
  display: flex;
  gap: 8px;
  align-items: center;
}

.source-count {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  font-weight: 600;
  color: #1a1d26;
}

.source-pct {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  font-weight: 600;
  color: #8b90a5;
  background: #f5f6fa;
  padding: 2px 6px;
  border-radius: 4px;
}

.source-bar {
  width: 100%;
  height: 5px;
  background: #f5f6fa;
  border-radius: 3px;
  overflow: hidden;
}

.source-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 1s cubic-bezier(0.16, 1, 0.3, 1);
}

/* === DONUT LAYOUT === */
.donut-layout {
  display: flex;
  align-items: center;
  gap: 28px;
  justify-content: center;
}

.donut-chart-wrap {
  flex-shrink: 0;
}

.donut-legend {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.donut-legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.donut-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}

.donut-legend-info {
  display: flex;
  flex-direction: column;
}

.donut-legend-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
}

.donut-legend-val {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  color: #8b90a5;
}

/* === MEDIUM ITEMS === */
.medium-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #eaecf2;
}

.medium-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.medium-item:first-child {
  padding-top: 0;
}

.medium-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.medium-info {
  flex: 1;
}

.medium-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
}

.medium-sub {
  font-size: 11px;
  color: #8b90a5;
}

.medium-stats {
  text-align: right;
}

.medium-count {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
}

.medium-pct {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  color: #8b90a5;
}

/* === GEO ITEMS === */
.geo-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eaecf2;
}

.geo-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.geo-item:first-child {
  padding-top: 0;
}

.geo-flag {
  font-size: 20px;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
}

.geo-info {
  flex: 1;
  min-width: 0;
}

.geo-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
  margin-bottom: 4px;
}

.geo-bar {
  width: 100%;
  height: 4px;
  background: #f5f6fa;
  border-radius: 2px;
  overflow: hidden;
}

.geo-bar-fill {
  height: 100%;
  border-radius: 2px;
  background: #556ee6;
  transition: width 1s cubic-bezier(0.16, 1, 0.3, 1);
}

.geo-stats {
  text-align: right;
  flex-shrink: 0;
}

.geo-count {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
}

.geo-pct {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  color: #8b90a5;
}

/* === CARD TABS === */
.card-tabs {
  display: flex;
  gap: 2px;
  background: #f5f6fa;
  padding: 2px;
  border-radius: 6px;
}

.card-tab {
  padding: 4px 10px;
  border: none;
  background: transparent;
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
}

.card-tab:hover {
  color: #1a1d26;
}

.card-tab.active {
  background: #fff;
  color: #1a1d26;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* === LANG ITEMS === */
.lang-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
}

.lang-code {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 11px;
  font-weight: 600;
  color: #4a5068;
  background: #f5f6fa;
  padding: 4px 8px;
  border-radius: 5px;
  width: 48px;
  text-align: center;
  flex-shrink: 0;
}

.lang-bar-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.lang-bar {
  flex: 1;
  height: 24px;
  background: #f5f6fa;
  border-radius: 6px;
  overflow: hidden;
}

.lang-bar-fill {
  height: 100%;
  border-radius: 6px;
  display: flex;
  align-items: center;
  padding-left: 10px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  transition: width 1s cubic-bezier(0.16, 1, 0.3, 1);
  white-space: nowrap;
  min-width: 28px;
}

.lang-count {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  font-weight: 600;
  color: #4a5068;
  flex-shrink: 0;
  width: 30px;
  text-align: right;
}

/* === MODERN TABLE === */
.table-wrapper {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  font-size: 13px;
}

.modern-table thead th {
  font-weight: 600;
  color: #8b90a5;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 12px;
  border-bottom: 2px solid #f1f3f5;
}

.modern-table tbody td {
  padding: 12px;
  border-bottom: 1px solid #f8f9fa;
}

.modern-table tbody tr:hover {
  background: #f8f9fa;
}

.campaign-name {
  font-weight: 500;
  color: #1a1d26;
}

.value-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #556ee6;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
}

/* === EMPTY STATES === */
.empty-state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  text-align: center;
}

.empty-state-card .empty-icon {
  width: 48px;
  height: 48px;
  background: #f5f6fa;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #b0b5c8;
  font-size: 22px;
}

.empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 4px;
}

.empty-desc {
  font-size: 12px;
  color: #8b90a5;
  line-height: 1.5;
  max-width: 220px;
}

.empty-state-page {
  text-align: center;
  padding: 80px 20px;
  color: #adb5bd;
}

.empty-state-page i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
}

/* === AI BANNER === */
.ai-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 0.25rem;
  margin-bottom: 16px;
  border: 1px solid;
  position: relative;
  overflow: hidden;
}

.ai-banner--clickable {
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s;
}

.ai-banner--clickable:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.ai-banner--clickable:active {
  transform: translateY(0);
}

.ai-banner::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background: linear-gradient(135deg, currentColor 0%, transparent 60%);
  pointer-events: none;
}

.ai-banner--info {
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border-color: #bfdbfe;
  color: #1e40af;
}

.ai-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 0.25rem;
}

.ai-banner--info .ai-banner-icon {
  background: linear-gradient(135deg, #556ee6, #3b5bdb);
}

.ai-banner-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.ai-banner-title {
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.3;
}

.ai-banner-subtitle {
  font-size: 0.75rem;
  font-weight: 400;
  opacity: 0.75;
  line-height: 1.3;
}

.ai-banner-badge {
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  white-space: nowrap;
}

.ai-banner--info .ai-banner-badge {
  background: rgba(85, 110, 230, 0.12);
  color: #1e40af;
}

.ai-banner-close {
  background: none;
  border: none;
  color: inherit;
  opacity: 0.5;
  cursor: pointer;
  padding: 4px;
  font-size: 18px;
  line-height: 1;
}

.ai-banner-close:hover {
  opacity: 1;
}

/* === UTM TUTORIAL === */
.utm-tutorial {
  padding: 8px 0;
}

.utm-section {
  margin-bottom: 24px;
}

.utm-example {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
}

.utm-example code {
  color: #556ee6;
  font-size: 13px;
  word-break: break-all;
}

.utm-use-case {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
}

.use-case-title {
  font-weight: 600;
  font-size: 13px;
  color: #2c3e50;
  margin-bottom: 8px;
}

.use-case-url code {
  font-size: 12px;
  color: #556ee6;
  word-break: break-all;
}

/* === DATE PICKER DROPDOWN === */
.sa-date-picker-wrap {
  position: relative;
}

.sa-date-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  box-shadow: 0 8px 24px rgba(26, 29, 38, 0.12);
  padding: 16px;
  z-index: 100;
  min-width: 220px;
}

.sa-date-dropdown-label {
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
  display: block;
}

.sa-date-dropdown-label + .sa-date-dropdown-label {
  margin-top: 10px;
}

.sa-date-dropdown-input {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  font-size: 13px;
  color: #1a1d26;
  outline: none;
  transition: border-color 0.2s;
  margin-bottom: 4px;
}

.sa-date-dropdown-input:focus {
  border-color: #556ee6;
}

.sa-date-dropdown-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.sa-date-dropdown-btn {
  flex: 1;
  padding: 7px 12px;
  border: none;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sa-date-dropdown-btn.cancel {
  background: #f5f6fa;
  color: #8b90a5;
}

.sa-date-dropdown-btn.cancel:hover {
  background: #eaecf2;
}

.sa-date-dropdown-btn.apply {
  background: #556ee6;
  color: #fff;
}

.sa-date-dropdown-btn.apply:hover {
  background: #3b5bdb;
}

/* === RESPONSIVE === */
@media (max-width: 1100px) {
  .kpi-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .cards-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .sa-header {
    flex-direction: column;
  }

  .sa-header-right {
    flex-wrap: wrap;
  }

  .donut-layout {
    flex-direction: column;
  }
}
</style>

<style>
/* Scrollbar do modal UTM (sem scoped para alcançar o modal no body) */
.modal-body {
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}
</style>
